import { Inject, Injectable } from '@angular/core';
import {
  DwAttributeType,
  DwContextDefinition,
  DwControlType,
  DwCustomContextBase,
  DwCustomContextDefinition,
  DwFieldType,
  DwMdAttribute,
  DwOrmDataService,
  DwOrmDataServiceToken,
  DwQueryMdAttribute,
  DwSecurityTopics,
  DwSecurityUserService
} from '@devwareapps/devware-cap';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  AppMetaData,
  RoStudentPaymentMethodEntity,
  StudentCourseEntity
} from '../../../meta-data/app-meta-data.service';

export const CurrentUserContext = 'currentUserContext';

interface CurrentUserContextData {
  TrainingStatus: string;
  HasTrainingActivityLastMonth: boolean;
  LastTrainingActivityDateTime: string;
  HasDefaultPayment: boolean;
  DefaultPaymentName: string;
}

@DwCustomContextDefinition({
  keyName: CurrentUserContext,
  display: 'Current User Context',
  isGlobal: true,
})
@Injectable()
export class CurrentUserContextService extends DwCustomContextBase {
  constructor(
    private dwSecurityUserService: DwSecurityUserService,
    @Inject(DwOrmDataServiceToken) private dwOrmDataService: DwOrmDataService,
  ) {
    super();
  }

  setupContextDefinition(contextDef: DwContextDefinition) {
    contextDef.noCache = true;
    contextDef.data = {};
    contextDef.cacheInvalidationTopics = [DwSecurityTopics.SECURITY_CHANGED];
    contextDef.cacheInvalidationTopics.push(
      DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.StudentCourse.ItemDetail.itemName),
    );
    contextDef.cacheInvalidationTopics.push(
      DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.PaymentMethod.ItemDetail.itemName),
    );
    contextDef.cacheInvalidationTopics.push(
      DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.RoStudentPaymentMethod.ItemDetail.itemName),
    );
    contextDef.attributes = this.getContextAttributes();
    contextDef.getCustomContextFunction = () => this.loadCurrentUserContext();
  }

  private getContextAttributes(): DwQueryMdAttribute[] {
    const attributes: DwQueryMdAttribute[] = [];

    this.addProperty(attributes, 'TrainingStatus', 'Training Status');
    this.addProperty(attributes, 'HasTrainingActivityLastMonth', 'Has Training Activity Last Month', DwAttributeType.YesNo, DwControlType.Checkbox);
    this.addProperty(attributes, 'LastTrainingActivityDateTime', 'Last Training Activity', DwAttributeType.DateTime, DwControlType.DateTimePicker);
    this.addProperty(attributes, 'HasDefaultPayment', 'Has Default Payment', DwAttributeType.YesNo, DwControlType.Checkbox);
    this.addProperty(attributes, 'DefaultPaymentName', 'Default Payment');

    return attributes;
  }

  private loadCurrentUserContext(): Observable<CurrentUserContextData> {
    const user = this.dwSecurityUserService.securityContext?.ApplicationUser;

    if (!user?.UserId) {
      return of(this.createEmptyContext());
    }

    const studentId = this.getExtendedAttributeInt(user, 'StudentId');

    return combineLatest([
      this.loadTrainingContext(studentId),
      this.loadPaymentContext(user.UserId),
    ]).pipe(
      map(([trainingContext, paymentContext]) => ({
        ...this.createEmptyContext(),
        ...trainingContext,
        ...paymentContext,
      })),
    );
  }

  private loadTrainingContext(studentId: number): Observable<Partial<CurrentUserContextData>> {
    if (!studentId) {
      return of({
        TrainingStatus: this.formatTrainingStatus(false),
        HasTrainingActivityLastMonth: false,
        LastTrainingActivityDateTime: null,
      });
    }

    const query = AppMetaData.StudentCourse.CreateQueryBuilder();
    query.addFilterAnd(filters => filters.Equal(field => field.StudentId, studentId));

    return this.dwOrmDataService.executeQuery<StudentCourseEntity>(query).pipe(
      map(studentCourses => {
        const lastActivityDateTime = this.getMostRecentActivityDate(studentCourses);
        const hasTrainingActivityLastMonth = this.isWithinLastMonth(lastActivityDateTime);

        return {
          TrainingStatus: this.formatTrainingStatus(hasTrainingActivityLastMonth),
          HasTrainingActivityLastMonth: hasTrainingActivityLastMonth,
          LastTrainingActivityDateTime: lastActivityDateTime,
        };
      }),
    );
  }

  /** HTML for the profile Account Status card (template is ishtml=true). */
  private formatTrainingStatus(isActive: boolean): string {
    if (!isActive) {
      return 'Inactive';
    }

    return '<span class="ao-status-dot" aria-hidden="true"></span>Active';
  }

  private loadPaymentContext(userId: number): Observable<Partial<CurrentUserContextData>> {
    const query = AppMetaData.RoStudentPaymentMethod.CreateQueryBuilder();
    query.query.FieldSettings.LoadAllLookupDisplayFields = true;
    query.addFilterAnd(filters => filters.Equal(field => field.UserId, userId));

    return this.dwOrmDataService.executeQuery<RoStudentPaymentMethodEntity>(query).pipe(
      map(paymentMethods => {
        const defaultPaymentMethod = (paymentMethods || []).find(paymentMethod => paymentMethod?.IsDefault && paymentMethod?.IsActive !== false);

        return {
          HasDefaultPayment: !!defaultPaymentMethod,
          DefaultPaymentName: this.formatDefaultPaymentName(defaultPaymentMethod?.DisplayName),
        };
      }),
    );
  }

  private getMostRecentActivityDate(studentCourses: StudentCourseEntity[]): string {
    const activityDates = (studentCourses || [])
      .map(studentCourse => studentCourse?.LastActivityDateTime)
      .filter(lastActivityDateTime => !!lastActivityDateTime)
      .sort((left, right) => new Date(right).getTime() - new Date(left).getTime());

    return activityDates[0] || null;
  }

  private isWithinLastMonth(lastActivityDateTime: string): boolean {
    if (!lastActivityDateTime) {
      return false;
    }

    const lastActivity = new Date(lastActivityDateTime);

    if (Number.isNaN(lastActivity.getTime())) {
      return false;
    }

    const cutoffDate = new Date();
    cutoffDate.setMonth(cutoffDate.getMonth() - 1);

    return lastActivity.getTime() >= cutoffDate.getTime();
  }

  private formatDefaultPaymentName(displayName: string): string {
    if (!displayName) {
      return null;
    }

    const normalizedDisplayName = displayName.trim();

    if (normalizedDisplayName.includes('****')) {
      return normalizedDisplayName;
    }

    const cardMatch = normalizedDisplayName.match(/^([A-Za-z]+)\s+[*]*\s*(\d{4})$/);

    if (cardMatch) {
      const brand = cardMatch[1].charAt(0).toUpperCase() + cardMatch[1].slice(1).toLowerCase();
      return `${brand} **** ${cardMatch[2]}`;
    }

    return normalizedDisplayName;
  }

  private getExtendedAttributeInt(user: any, attributeName: string): number {
    const extendedAttribute = user?.ExtendedAttributes?.find(attribute => attribute?.AttrName === attributeName);
    const attributeValue = extendedAttribute?.AttrValue;

    if (!attributeValue) {
      return null;
    }

    const parsedValue = parseInt(attributeValue, 10);

    return Number.isNaN(parsedValue) ? null : parsedValue;
  }

  private createEmptyContext(): CurrentUserContextData {
    return {
      TrainingStatus: this.formatTrainingStatus(false),
      HasTrainingActivityLastMonth: false,
      LastTrainingActivityDateTime: null,
      HasDefaultPayment: false,
      DefaultPaymentName: null,
    };
  }

  // private addProperty(
  //   attributes: DwQueryMdAttribute[],
  //   name: string,
  //   display: string,
  //   attrType: DwAttributeType = DwAttributeType.Text,
  //   controlType: DwControlType = DwControlType.Textbox,
  // ): DwQueryMdAttribute {
  //   const attribute: DwMdAttribute = {
  //     AttributeName: name,
  //     Display: display,
  //     AttributeType: attrType,
  //     MetaDataControlTypeId: controlType,
  //     IsLookup: false,
  //   };

  //   const contextAttribute: DwQueryMdAttribute = {
  //     Attribute: attribute,
  //     FieldType: DwFieldType.Standard,
  //     AttributeDisplay: attribute.Display,
  //     AttributeType: attribute.AttributeType,
  //     AttributeName: attribute.AttributeName,
  //     HasDisplayField: false,
  //     DisplayAttributeName: attribute.AttributeName,
  //   };

  //   attributes.push(contextAttribute);

  //   return contextAttribute;
  // }
}
